import React, { memo, useState } from 'react';
import { useIntl } from 'react-intl';
import PlaylistSearchRow from './PlaylistSearchRow';
import { usePlaylistContext } from '../../../../context/PlaylistProvider';
import { useSnackbarContext } from '../../../../context/SnackbarProvider';
import { useUserContext } from '../../../../context/UserContext';

import {
  PlaylistLearnNodeItem,
  PlaylistProps,
  PlaylistItemProps,
  PlaylistStateType,
} from '@osmosis/shared/types/playlist';
import { PlaylistSearchHitType } from '../../../../types/search';
import useModal from '../../../../hooks/useModal';
import ExpiredUserLockoutModal from '../../../../components/learn/ExpiredUserModal';
import { ExpiredUserModalType } from '../../../../constants/ExpiredUserModalConfig';
import { getAddToPlaylistMessage } from '../../../../constants/playlist';

type Props = {
  playlist: PlaylistProps;
  hits: PlaylistSearchHitType[];
  handleRemove: (items: PlaylistLearnNodeItem[]) => Promise<void>;
};

const SearchResults = (props: Props) => {
  const { hits, playlist, handleRemove } = props;

  const intl = useIntl();
  const { addPlaylistVideos } = usePlaylistContext();
  const { onAddSnackBar } = useSnackbarContext();
  const { hasPrime } = useUserContext();

  const playlistNonPrimeModal = useModal();
  const [ariaLiveMessage, setAriaLiveMessage] = useState<string>('');

  const handleAddToPlaylist = async (
    items: PlaylistLearnNodeItem[],
    options: { showSnackbar: boolean } = { showSnackbar: true },
  ) => {
    if (hasPrime) {
      const res = (await addPlaylistVideos({
        items,
        playlistName: playlist.title,
        playlistUrl: playlist.url,
        ...(options.showSnackbar ? { onAddSnackBar } : {}),
      })) as { success: PlaylistStateType; playlist: PlaylistProps };

      const message = getAddToPlaylistMessage({
        intl,
        items,
        playlistName: playlist.title,
      });
      setAriaLiveMessage(message);

      const quizData = res.playlist?.media || { fc: [], ques: {} };

      const subType = res.playlist?.subType;

      const videos: PlaylistSearchHitType[] = items
        .map((i) => hits.find((h) => h.learnNodeID === i.learnNodeID))
        .filter((v): v is PlaylistSearchHitType => !!v);

      playlist.list = [
        ...playlist.list,
        ...videos.map((v) => {
          let accessState = {};
          if (v.subProducts?.length === 1) {
            accessState = v.subProducts?.reduce<Record<string, string>>(
              (acc, curr) => {
                acc[curr.groupName] = curr.state;
                return acc;
              },
              {},
            );
          }

          return {
            title: v.concept,
            data: {
              nodeID: v.learnNodeID,
              progress: v.watchPercent,
              thumbnail: v.thumb,
              title: v.concept,
              quizData,
              subType,
            },
            opts: {},
            accessState: accessState,
          } as PlaylistItemProps;
        }),
      ];
    } else {
      playlistNonPrimeModal.show();
    }
  };

  const handleReplaceVideo = async (items: PlaylistLearnNodeItem[]) => {
    const savedPlaylistItem = localStorage.getItem('playlistItem');
    await handleAddToPlaylist(items, { showSnackbar: false });
    if (savedPlaylistItem) {
      const parsedItem = JSON.parse(savedPlaylistItem);
      await handleRemove([
        {
          learnNodeID: parsedItem.data.nodeID,
          name: parsedItem.title,
        },
      ]);
      onAddSnackBar({
        message: intl.formatMessage(
          {
            id: 'playlist.snackbar.replace',
            defaultMessage: '{oldName} was replaced with {newName}',
          },
          {
            oldName: parsedItem.title,
            newName: items[0].name,
          },
        ),
        variant: 'red',
      });
    }
  };

  return (
    <>
      <ExpiredUserLockoutModal
        {...playlistNonPrimeModal}
        modalType={ExpiredUserModalType.PLAYLIST}
      />
      {hits.length > 0 &&
        hits.map((video) => {
          const isInPlaylist = !!playlist.list.find(
            (v) => v.data.nodeID === video.learnNodeID,
          );
          const toBeRetiredTag = video.tags?.find(
            ({ tag }) => tag.toLowerCase() === 'to be retired',
          );

          return (
            <PlaylistSearchRow
              key={video.learnNodeID}
              video={video}
              inPlaylist={isInPlaylist}
              onAdd={handleAddToPlaylist}
              onReplace={handleReplaceVideo}
              toBeRetiredTag={toBeRetiredTag}
            />
          );
        })}
      <div aria-live="polite" className="sr-only">
        {ariaLiveMessage}
      </div>
    </>
  );
};

export default memo(SearchResults);
